import React, { ReactNode, useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Avatar,
  Tooltip,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  styled,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import logoutIcon from '../../../assets/branding/icons/logout.svg';
import { mrLogo } from '../../../assets/branding';
import { useAuth } from '../../../features/auth/hooks/useAuth';
import HeaderSkeleton from './HeaderSkeleton';
import { HELP_URL, UserStatus } from '../../constant/constant';
import { openMailBox } from '../../../utils/openMailBox';
import { setUserStatus } from '../../store/slices/auth.slice';

const StyledMenuItem = styled('div')(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const ProfileInfoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
}));

interface HeaderProps {
  children?: ReactNode;
  showLogo?: boolean;
}

const Header: React.FC<HeaderProps> = ({ children, showLogo }) => {
  const navigate = useNavigate();
  const personalDetails = useSelector((state: RootState) => state.personalDetails);
  const basicInfo = useSelector((state: RootState) => state.basicInfo);
  const { logout, clearAllApiErrors, clearSuccess, isLoadingProfile } = useAuth();
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const avatarRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const userStatus = useSelector((state: RootState) => state.basicInfo.currentStatus);
  const dispatch = useDispatch();
  const  isDeletedUser = basicInfo.deleted;
  const handleAvatarClick = () => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    } else if (avatarRef.current) {
      const rect = avatarRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom,
        left: rect.right - 240,
      });
      setIsMenuOpen(true);
    }
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLogout = useCallback(() => {
    if (clearAllApiErrors) {
      clearAllApiErrors();
    }

    if (clearSuccess) {
      clearSuccess();
    }

    handleCloseMenu();
    setTimeout(() => {
      logout();
      navigate('/auth#login');
    }, 50);
  }, [logout, navigate, clearAllApiErrors, clearSuccess]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        avatarRef.current &&
        !avatarRef.current.contains(event.target as Node)
      ) {
        handleCloseMenu();
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    function handleScroll() {
      if (isMenuOpen && avatarRef.current) {
        const rect = avatarRef.current.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom,
          left: rect.right - 240,
        });
      }
    }

    if (isMenuOpen) {
      window.addEventListener('scroll', handleScroll);
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (userStatus === UserStatus.EXITED_BY_ADMIN) {
      navigate('/auth#login');
      handleLogout();
      dispatch(setUserStatus(UserStatus.EXITED_BY_ADMIN));
      sessionStorage.setItem('isExitedByAdmin', 'true');
    }
    if (isDeletedUser) {
      navigate('/auth#login');
      handleLogout();
      dispatch(setUserStatus(UserStatus.DELETED_BY_ADMIN));
      sessionStorage.setItem('isDeletedUser', 'true');
    }

  }, [userStatus, navigate, handleLogout, isDeletedUser]);

  if (isLoadingProfile) {
    return <HeaderSkeleton />;
  }

  return (
    <AppBar
      position='relative'
      color='default'
      elevation={0}
      sx={{
        borderBottom: '1px solid #E0E0E0',
        zIndex: 1100,
        borderRadius: 0,
        backgroundColor: '#FFFFFF',
      }}
    >
      <Toolbar sx={{ height: 64, px: { xs: 1, sm: 2 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexGrow: 0,
          }}
        >
          {children}
          {showLogo && (
            <Box ml={children ? 2 : 0}>
              <img src={mrLogo} alt='MindRight Logo' style={{ width: 90 }} />
            </Box>
          )}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexGrow: 0,
          }}
        >
          <Tooltip title='Help'>
            <IconButton onClick={() => openMailBox()} size='large' color='inherit' sx={{ ml: { xs: 0.5, sm: 1 } }}>
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
          {basicInfo?.currentStatus !== UserStatus.SIGNED_UP && <Tooltip title='Settings'>
            <IconButton onClick={() => navigate('/account-settings')} size='large' color='inherit' sx={{ ml: { xs: 0.5, sm: 1 } }}>
              <SettingsOutlinedIcon />
            </IconButton>
          </Tooltip>}


          <Box ref={avatarRef} sx={{ ml: { xs: 0.5, sm: 2 }, position: 'relative' }}>
            <Tooltip title={personalDetails?.firstName || 'User Profile'}>
              <Avatar
                onClick={handleAvatarClick}
                aria-haspopup='true'
                aria-expanded={isMenuOpen ? 'true' : 'false'}
                aria-controls={isMenuOpen ? 'user-menu' : undefined}
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: '#14192C',
                  cursor: 'pointer',
                }}
              >
                {personalDetails?.firstName?.charAt(0) || 'M'}
              </Avatar>
            </Tooltip>

            {isMenuOpen && menuPosition && (
              <div
                ref={menuRef}
                id='user-menu'
                role='menu'
                aria-orientation='vertical'
                style={{
                  position: 'fixed',
                  top: `${menuPosition.top}px`,
                  left: `${menuPosition.left}px`,
                  width: '240px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
                  zIndex: 1500,
                }}
              >
                <ProfileInfoBox>
                  <Typography
                    variant='subtitle1'
                    sx={{
                      fontWeight: 500,
                      mb: 0.5,
                      userSelect: 'text',
                    }}
                  >
                    {personalDetails?.firstName && personalDetails?.lastName
                      ? `${personalDetails.firstName} ${personalDetails.lastName}`
                      : 'Welcome to MindRight'}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{
                      wordBreak: 'break-word',
                      opacity: 0.85,
                      userSelect: 'text',
                    }}
                  >
                    {personalDetails?.email || 'user@example.com'}
                  </Typography>
                </ProfileInfoBox>

                <Divider sx={{ my: 0.5 }} />

                <StyledMenuItem
                  onClick={handleLogout}
                  role='menuitem'
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleLogout();
                    }
                  }}
                >
                  <ListItemIcon data-testid='LogOutLi' sx={{ minWidth: 36 }}>
                    <img src={logoutIcon} alt='Log Out' />
                  </ListItemIcon>
                  <ListItemText
                    primary='Log Out'
                    slotProps={{
                      primary: {
                        sx: { fontWeight: 450 },
                      },
                    }}
                  />
                </StyledMenuItem>
              </div>
            )}
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default React.memo(Header);